import React, { useEffect, useMemo, useState } from 'react';
import { Newspaper, Plus, Check } from 'lucide-react';
import { Feed } from '../types';
import { Translations, Language } from '../i18n/translations';

interface OnboardingPickerProps {
  language: Language;
  onAddFeeds: (feeds: Feed[]) => void;
  onOpenSettings: () => void;
  onLearnMore: () => void;
  t: Translations;
}

interface RemoteFeedEntry {
  publication_name: string;
  publication_website_uri: string;
  publication_rss_feed_uri: string;
  category?: string;
  bot_protection?: boolean;
}

interface ExampleFeed {
  name: string;
  url: string;
}

// Empty-state onboarding: the user can toggle several recommended sources
// and commit them all at once instead of being kicked out of the gallery
// after the first pick. Once any feed is added the picker unmounts (the
// empty-state condition flips), so all the visual state is local.
export const OnboardingPicker: React.FC<OnboardingPickerProps> = ({
  language,
  onAddFeeds,
  onOpenSettings,
  onLearnMore,
  t,
}) => {
  const [exampleFeeds, setExampleFeeds] = useState<ExampleFeed[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const country = language === 'pl' ? 'POL' : 'GBR';
        const res = await fetch(`/api/feed-list?country=${country}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: RemoteFeedEntry[] = await res.json();
        const seen = new Set<string>();
        const out: ExampleFeed[] = [];
        for (const entry of data) {
          if (entry.bot_protection || !entry.publication_rss_feed_uri) continue;
          const name = entry.publication_name;
          if (seen.has(name)) continue;
          seen.add(name);
          out.push({ name, url: entry.publication_rss_feed_uri });
        }
        if (!cancelled) setExampleFeeds(out);
      } catch {
        // Silent fail — fallback UI links the user into Settings.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [language]);

  const toggle = (url: string): void => {
    setSelectedUrls(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const selectedFeeds = useMemo<Feed[]>(
    () => exampleFeeds.filter(f => selectedUrls.has(f.url)),
    [exampleFeeds, selectedUrls],
  );

  const handleCommit = (): void => {
    if (selectedFeeds.length === 0) return;
    onAddFeeds(selectedFeeds);
  };

  const addLabel = selectedFeeds.length === 0
    ? t.onboardingAddNone
    : (selectedFeeds.length === 1 ? t.onboardingAddOne : t.onboardingAddMany.replace('{count}', String(selectedFeeds.length)));

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center py-8 sm:py-10 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 px-6">
        <Newspaper className="w-12 h-12 mx-auto text-gray-400 mb-3" aria-hidden="true" />
        <h2 className="text-2xl sm:text-3xl font-bold font-serif mb-2 dark:text-white">{t.welcome}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
          {t.onboardingPickPrompt}
        </p>
      </div>

      <div className="mt-4 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4">
        <h3 className="font-bold mb-3 text-sm sm:text-base text-gray-900 dark:text-white">
          {t.exampleFeeds}
        </h3>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t.loadingExampleFeeds}</p>
        ) : exampleFeeds.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t.onboardingNoFeeds}</p>
        ) : (
          <>
            <div role="group" aria-label={t.exampleFeeds} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {exampleFeeds.map(feed => {
                const selected = selectedUrls.has(feed.url);
                return (
                  <button
                    key={feed.url}
                    onClick={() => toggle(feed.url)}
                    aria-pressed={selected}
                    aria-label={`${selected ? t.onboardingDeselect : t.onboardingSelect}: ${feed.name}`}
                    className={`text-left px-3 py-2 border-2 text-xs sm:text-sm flex items-center justify-between gap-2 ${
                      selected
                        ? 'border-blue-600 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-100'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-blue-700 dark:text-blue-400'
                    }`}
                  >
                    <span className="font-medium truncate">{feed.name}</span>
                    {selected ? (
                      <Check className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    ) : (
                      <Plus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={handleCommit}
                disabled={selectedFeeds.length === 0}
                className="px-4 py-2 text-sm font-semibold bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                {addLabel}
              </button>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <button
                  onClick={onOpenSettings}
                  className="text-blue-700 dark:text-blue-400 hover:underline"
                >
                  {t.onboardingOwnUrl}
                </button>
                <button
                  onClick={onLearnMore}
                  className="text-gray-500 dark:text-gray-400 hover:underline"
                >
                  {t.learnMore}
                </button>
              </div>
            </div>
          </>
        )}

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          {t.onboardingEditLater}
        </p>
      </div>
    </div>
  );
};
